import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, keys, queryClient, useHelpTexts, useMe } from '../../api.ts';
import { loginSchema, type LoginInput } from '../../../shared/schemas.ts';
import type { ReportListItem, SessionUser } from '../../../shared/types.ts';
import { ErrorBox, Field, Spinner, useDocumentTitle } from '../../components/ui.tsx';

/** Sallitaan vain sovelluksen sisäiset paluuosoitteet */
export function safeNext(next: string | null): string | null {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : null;
}

export function LoginPage() {
  useDocumentTitle('Kirjaudu');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const help = useHelpTexts();
  const reports = useQuery({ queryKey: keys.reports, queryFn: () => api.get<ReportListItem[]>('/reports') });
  const { register, handleSubmit, formState } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });
  const login = useMutation({
    mutationFn: (d: LoginInput) => api.post<{ user: SessionUser }>('/auth/login', d),
    onSuccess: ({ user }) => {
      queryClient.setQueryData(keys.me, user);
      navigate(safeNext(params.get('next')) ?? `/users/${encodeURIComponent(user.id)}`, { replace: true });
    },
  });

  if (me) return <Navigate to={`/users/${encodeURIComponent(me.id)}`} replace />;

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="card">
          <h1>DXA.fi</h1>
          <p className="muted">OH DX -taulukot: DXCC-tilanteet, Divisions, Marathon ja Band-Modes.</p>
          {help.data?.Kirjautuminen && <div className="help-panel">{help.data.Kirjautuminen}</div>}
          <form className="form" onSubmit={handleSubmit((d) => login.mutate(d))} noValidate>
            <Field label="Kutsu" error={formState.errors.username} htmlFor="username">
              <input
                id="username"
                className="input mono"
                autoComplete="username"
                autoCapitalize="characters"
                autoFocus
                {...register('username')}
              />
            </Field>
            <Field label="Salasana" error={formState.errors.password} htmlFor="password">
              <input id="password" className="input" type="password" autoComplete="current-password" {...register('password')} />
            </Field>
            <ErrorBox error={login.error} />
            <div className="form-actions">
              <button className="btn btn-primary" type="submit" disabled={login.isPending}>
                {login.isPending ? <Spinner /> : 'Kirjaudu sisään'}
              </button>
              <Link className="btn btn-ghost" to="/register">
                Rekisteröidy
              </Link>
            </div>
          </form>
        </div>
        {!!reports.data?.length && (
          <div className="auth-links">
            <span className="faint">Taulukot:</span>
            {reports.data.slice(0, 6).map((r) => (
              <Link key={r.id} to={`/reports/${r.id}`}>
                {r.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
